import { Suspense, lazy } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useGetFeaturedAvatarQuery, type AvatarItem } from '@/services/avatarApi';
import { cn } from '@/lib/cn';
import avatar3dCopy from '@/i18n/copy/avatar-3d.json';
import { getLastPickedAnimation } from '@/lib/lastPickedAnimation';

const ModelViewer = lazy(() => import('./ModelViewer'));
const AnimatedModelViewer = lazy(() => import('./AnimatedModelViewer'));

const QUEUED_OVERLAY_STATES = new Set([
  'queued',
  'claimed',
  'meshing',
  'validating',
  'uploading',
]);

const HEARTBEAT_SLOW_THRESHOLD_MS = 5 * 60 * 1000;

function queuedOverlayText(mesh: NonNullable<AvatarItem['mesh']>): string {
  if (!mesh.lastHeartbeatAt) return avatar3dCopy['avatar3d.status.queued'];
  const last = new Date(mesh.lastHeartbeatAt).getTime();
  if (!Number.isFinite(last)) return avatar3dCopy['avatar3d.status.queued'];
  const isSlow = Date.now() - last > HEARTBEAT_SLOW_THRESHOLD_MS;
  return isSlow
    ? avatar3dCopy['avatar3d.status.queuedSlow']
    : avatar3dCopy['avatar3d.status.queued'];
}

interface FeaturedAvatarProps {
  className?: string;
}

/**
 * Hub centrepiece: the user's latest AI-generated avatar shown floating
 * (transparent background, no card frame) above the QLASH mascot. Tap routes
 * to `/avatars` so the user can pick a different one or generate a new one.
 *
 * Render branches:
 *   - mesh.status === 'ready'         → <ModelViewer> (lazy, GLB rotation)
 *   - mesh.status in QUEUED_OVERLAY_* → PNG 2D + overlay copy
 *   - everything else                 → PNG 2D, no overlay
 *
 * If the user has zero avatars yet, surfaces a compact "Create your first
 * avatar" CTA pointing to `/avatars/new` — same UX pattern as Profile's
 * MyAvatars empty state.
 */
export function FeaturedAvatar({ className }: FeaturedAvatarProps) {
  const { t } = useTranslation('general');
  const { data: featured, isLoading } = useGetFeaturedAvatarQuery();

  if (isLoading) {
    return (
      <div className={cn('w-full animate-pulse rounded-2xl bg-bg-secondary/30', className)} />
    );
  }

  if (!featured) {
    return (
      <div className={cn('flex items-center justify-center', className)}>
        <Link
          to="/avatars/new"
          className={cn(
            'flex w-full max-w-sm items-center gap-3 rounded-2xl border border-dashed border-brand/40 bg-bg-secondary/50 p-4',
            'transition-opacity active:opacity-80',
          )}
        >
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-brand/15 text-brand">
            <Sparkles aria-hidden className="h-5 w-5" />
          </span>
          <span className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-fg">
              {t('avatars.create_first', { defaultValue: 'Create your first avatar' })}
            </p>
            <p className="truncate text-xs text-fg-muted">
              {t('avatars.create_hint', { defaultValue: 'AI-generated, in seconds.' })}
            </p>
          </span>
          <ChevronRight aria-hidden className="h-4 w-4 shrink-0 text-fg-muted" />
        </Link>
      </div>
    );
  }

  const meshStatus = featured.mesh?.status;
  const pngSrc = featured.imageUrl || featured.thumbnailUrl;

  if (featured.mesh?.animationsGlbUrl) {
    const poster = featured.mesh.animationsPosterUrl ?? featured.mesh.posterUrl ?? featured.thumbnailUrl;
    const lastPicked = getLastPickedAnimation(featured._id);
    // Use the user's last picked animation on AvatarDetail; fall back to 'idle'.
    const animationName = lastPicked ?? 'idle';
    return (
      <div
        className={cn(
          'group relative flex flex-col items-center justify-center',
          className,
        )}
      >
        <Suspense
          fallback={
            <img
              src={poster}
              alt={featured.title}
              className="h-full max-h-full w-auto max-w-full object-contain drop-shadow-[0_8px_24px_rgba(0,231,223,0.25)]"
              loading="eager"
            />
          }
        >
          <AnimatedModelViewer
            src={featured.mesh.animationsGlbUrl}
            poster={poster}
            animationName={animationName}
            autoplay
            cameraOrbit="90deg 90deg 105%"
            cameraControls
            className="h-full max-h-full w-full max-w-full"
            style={{ backgroundColor: 'transparent', filter: 'drop-shadow(0 8px 24px rgba(0,231,223,0.25))' }}
          />
        </Suspense>
        <Link
          to="/avatars"
          className="absolute bottom-1 rounded-full bg-bg-secondary/70 px-3 py-1 text-[10px] font-medium uppercase tracking-wider text-fg-muted backdrop-blur transition-opacity active:opacity-80"
        >
          {t('avatars.tap_change', { defaultValue: 'Tap to change' })}
        </Link>
      </div>
    );
  }

  if (featured.mesh?.glbUrl) {
    const poster = featured.mesh.posterUrl ?? featured.thumbnailUrl ?? featured.imageUrl;
    return (
      <Link
        to="/avatars"
        className={cn(
          'group relative flex flex-col items-center justify-center transition-transform active:scale-[0.98]',
          className,
        )}
      >
        <Suspense
          fallback={
            <img
              src={poster}
              alt={featured.title}
              className="h-full max-h-full w-auto max-w-full object-contain drop-shadow-[0_8px_24px_rgba(0,231,223,0.25)]"
              loading="eager"
            />
          }
        >
          <ModelViewer
            src={featured.mesh.glbUrl}
            poster={poster}
            alt={featured.title}
            className="h-full max-h-full w-full max-w-full"
            style={{ backgroundColor: 'transparent', filter: 'drop-shadow(0 8px 24px rgba(0,231,223,0.25))' }}
          />
        </Suspense>
        <span className="absolute bottom-1 text-[10px] font-medium uppercase tracking-wider text-fg-muted">
          {t('avatars.tap_change', { defaultValue: 'Tap to change' })}
        </span>
      </Link>
    );
  }

  const showQueuedOverlay = meshStatus !== undefined && QUEUED_OVERLAY_STATES.has(meshStatus);
  const overlayText = showQueuedOverlay && featured.mesh ? queuedOverlayText(featured.mesh) : null;

  return (
    <Link
      to="/avatars"
      className={cn(
        'group relative flex flex-col items-center justify-center transition-transform active:scale-[0.98]',
        className,
      )}
    >
      <img
        src={pngSrc}
        alt={featured.title}
        className="h-full max-h-full w-auto max-w-full object-contain drop-shadow-[0_8px_24px_rgba(0,231,223,0.25)]"
        loading="eager"
      />
      {overlayText && (
        <div className="absolute inset-x-0 bottom-0 flex justify-center px-4 pb-6">
          <p className="max-w-xs rounded-xl bg-bg-secondary/85 px-3 py-2 text-center text-xs text-fg shadow-lg backdrop-blur">
            {overlayText}
          </p>
        </div>
      )}
      {!overlayText && (
        <span className="absolute bottom-1 text-[10px] font-medium uppercase tracking-wider text-fg-muted">
          {t('avatars.tap_change', { defaultValue: 'Tap to change' })}
        </span>
      )}
    </Link>
  );
}
